import React, { FC, ReactElement, useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormFieldT, SelectOptionT } from "../../models/models";
import SelectField from "./components/SelectField/SelectField";
import InputField from "./components/InputField/InputField";
import { getFieldName, getValidationSchemaFromFields } from "./helpers";
import { clss } from "../../utils/helpers/styleHelpers";
import TabField from "./components/TabField/TabField";
import CheckboxField from "./components/CheckboxField/CheckboxField";
import ImageField from "./components/ImageField/ImageField";
import DateField from "./components/DateField/Datefield";
import { isMobileOrTablet } from "../../utils/helpers/mediaQueries";
import TextAreaField from "./components/TextAreaField/TextAreaField";
import TextAreaWysiwygField from "./components/TextAreaWysiwygField/TextAreaWysiwygField";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { setIsFormDirty } from "../../store/slices/appSlice";
import { selectDirection } from "../../store/selectors/appSelectors";
import { useTranslate } from "../../utils/hooks/useTranslate";
import Button from "../Button/Button";

import styles from "./form.module.scss";

type FormPropTypes = {
  onSubmit: any;
  fields: FormFieldT[];
  formID: string;
  submitLabel?: string;
  withSubmitButton?: boolean;
  className?: string;
  elements?: ReactElement;
  columns?: number;
  handleDirty?: boolean;
  preventSubmitWithEnter?: boolean;
};

/**
 * Custom form component with any input (string, number, etc...) and select fields
 * Supports yup validation.
 *
 * Pass array of fields to the component when you declare it.
 * For changing order of elements add 'orderIndex' prop for field or 'order' style rule for usual element.
 *
 * Field declaration example
 * {
 *     name: 'firstName',
 *     label: 'First Name',
 *     validation: ValidatorsE.REQUIRED_STRING (you can use here custom yup scheme as well)
 *     type: 'text' by default
 *     options: (if you define it, it will use CustomSelect component instead of input)
 * }
 * */

const Form: FC<FormPropTypes> = (props) => {
  const {
    onSubmit,
    fields,
    formID,
    submitLabel = "submit",
    withSubmitButton = false,
    className = "",
    elements, // additional UI elements
    columns = 2,
    handleDirty = false,
    preventSubmitWithEnter,
  } = props;

  const filteredFields = fields.filter((field) => field.label !== null);
  const dispatch = useAppDispatch();
  const direction = useAppSelector(selectDirection);
  const { t } = useTranslate();

  // build validation schema based on fields data
  const schema = getValidationSchemaFromFields(filteredFields, t);

  const {
    register,
    handleSubmit,
    formState: { errors, touchedFields, isDirty, ...formState },
    control,
    clearErrors,
    trigger,
    getValues,
    setValue,
  } = useForm({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    handleDirty && isDirty && dispatch(setIsFormDirty(true));
  }, [isDirty]);

  const handleTriggerField = useCallback((e) => {
    trigger(e.detail.fieldNames).then((res) => {
      const formErrorsCustomEvent = new CustomEvent("formErrors", {
        detail: {
          noErrors: res,
          values: getValues(),
        },
      });
      document.dispatchEvent(formErrorsCustomEvent);
    });
  }, [getValues, trigger]);

  const handleUpdateFields = useCallback((e) => {
    const fieldData = e.detail;
    if (fieldData && Array.isArray(fieldData)) {
      fieldData.map(({ name, value }) => {
        setValue(name, value);
      });
    }
  }, [setValue]);

  useEffect(() => {
    const form = document.getElementById(formID);

    form?.addEventListener("triggerFields", handleTriggerField);
    document?.addEventListener("updateFields", handleUpdateFields);
    return () => {
      form?.removeEventListener("triggerFields", handleTriggerField);
      document?.removeEventListener("updateFields", handleUpdateFields);
    };
  }, [formID, handleTriggerField, handleUpdateFields, setValue]);

  const checkKeyDown = (e: any) => {
    if (e.code === "Enter") {
      e.preventDefault();
    }
  };

  const trimInputDataStrings = (data: any[], e: any) => {
    Object.keys(data).forEach((k) =>
      typeof data[k] === "string" ? (data[k] = data[k].trim()) : data[k]
    );
    onSubmit(data, e);
  };

  return (
    <form
      onSubmit={handleSubmit(trimInputDataStrings)}
      onKeyDown={(e) => preventSubmitWithEnter && checkKeyDown(e)}
      id={formID}
      className={clss([className, styles.wrapper])}
      // define amount of columns
      style={{
        gridTemplateColumns: `repeat(${isMobileOrTablet() ? 1 : columns}, 1fr)`,
        direction,
      }}
    >
      {filteredFields
        .filter((field) => field.label !== null)
        .map(
          (
            {
              name,
              label = "",
              type = "text",
              hintText, // text, shown on hover (i) icon
              selectOptions,
              tabOptions,
              orderIndex = 0,
              createNewOption,
              defaultValue,
              style = {},
              isMulti,
              extraContent,
              menuFooterContent,
              props,
              onChange: outerOnChange = () => { },
              onInput = () => { },
              onBlur = () => { },
              closeMenuOnSelect,
              placeholder,
              addAction,
              isClearable,
              capitalLetters,
              onlyLettersAndNumbers,
              onlyNumbers,
              controlledValue,
              customSelectPropsStyles,
              fileName,
              imageType,
              minEdgeLength,
              allowKeepOriginalRatio,
              requireSquare
            },
            index
          ) => {
            const fieldName = getFieldName(name, label);
            const onChange = (data) => {
              outerOnChange(data);
              // Clear error immediately
              errors[fieldName] && (errors[fieldName] = null);
              // Trigger validation only for Languages field
              // setTimeout ensures field.onChange in SelectField updates form value before validation
              if (name === "Languages") {
                setTimeout(() => {
                  trigger(fieldName);
                }, 0);
              }
            };

            // if select options exists, return CustomSelect component
            if (selectOptions) {
              const fieldError: Record<string, any> = errors[fieldName];
              // For multi-select, error is in fieldError?.message, for single select in fieldError?.value?.message
              const errorMessage = fieldError?.message || fieldError?.value?.message;

              return (
                <SelectField
                  key={fieldName}
                  name={fieldName}
                  label={label}
                  hintText={hintText}
                  errorMessage={errorMessage}
                  options={selectOptions as SelectOptionT[]}
                  control={control}
                  orderIndex={orderIndex}
                  createNewOption={createNewOption}
                  style={style}
                  props={props}
                  defaultValue={defaultValue}
                  onChange={onChange as () => void}
                  onInput={onInput as () => void}
                  isMulti={isMulti as boolean}
                  extraContent={extraContent}
                  menuFooterContent={menuFooterContent}
                  closeMenuOnSelect={closeMenuOnSelect as boolean}
                  placeholder={placeholder}
                  addAction={addAction}
                  isClearable={isClearable}
                  onBlur={onBlur}
                  controlledValue={controlledValue}
                  customSelectPropsStyles={customSelectPropsStyles}
                />
              );
            }

            // if tab options exists, return Tab component
            if (tabOptions)
              return (
                <TabField
                  key={fieldName}
                  options={tabOptions}
                  name={fieldName}
                  register={register}
                  label={label}
                  orderIndex={orderIndex}
                  errorMessage={
                    (errors[fieldName] as any)?.value.message as string
                  }
                  style={style}
                  onChange={onChange as () => void}
                  defaultValue={defaultValue}
                  extraContent={extraContent}
                />
              );

            if (type === "checkbox")
              return (
                <CheckboxField
                  key={fieldName}
                  name={fieldName}
                  label={label}
                  defaultValue={defaultValue}
                  register={register}
                  orderIndex={orderIndex}
                  errorMessage={errors[fieldName]?.message as string}
                  style={style}
                  onChange={onChange as () => void}
                  extraContent={extraContent}
                  props={props}
                />
              );

            if (type === "image")
              return (
                <ImageField
                  key={fieldName}
                  name={fieldName}
                  label={label}
                  register={register}
                  orderIndex={orderIndex}
                  errorMessage={errors[fieldName]?.message as string}
                  style={style}
                  onChange={onChange as () => void}
                  src={defaultValue}
                  extraContent={extraContent}
                  hintText={hintText}
                  fileName={fileName}
                  imageType={imageType}
                  minEdgeLength={minEdgeLength}
                  allowKeepOriginalRatio={allowKeepOriginalRatio}
                  requireSquare={requireSquare}
                />
              );

            if (type === "date")
              return (
                <DateField
                  key={fieldName}
                  name={fieldName}
                  label={label}
                  register={register}
                  hintText={hintText}
                  orderIndex={orderIndex}
                  errorMessage={errors[fieldName]?.message as string}
                  style={style}
                  onChange={onChange as () => void}
                  defaultValue={defaultValue}
                  props={props}
                  extraContent={extraContent}
                />
              );

            if (type === "textarea")
              return (
                <TextAreaField
                  key={fieldName}
                  name={fieldName}
                  label={label}
                  defaultValue={defaultValue}
                  hintText={hintText}
                  errorMessage={errors[fieldName]?.message as string}
                  register={register}
                  orderIndex={orderIndex}
                  style={style}
                  props={props}
                  onChange={onChange as () => void}
                  extraContent={extraContent}
                />
              );

            if (type === "wysiwyg")
              return (
                <TextAreaWysiwygField
                  key={fieldName}
                  name={fieldName}
                  label={label}
                  defaultValue={defaultValue}
                  hintText={hintText}
                  errorMessage={errors[fieldName]?.message as string}
                  register={register}
                  orderIndex={orderIndex}
                  style={style}
                  props={props}
                  onChange={onChange as () => void}
                  extraContent={extraContent}
                />
              );

            if (type === "title")
              return (
                <div key={fieldName} style={{ order: orderIndex, ...style }} className={props?.className}>
                  {label && <h2 className={props?.className}>{label}</h2>}
                </div>
              );

            return (
              <InputField
                key={fieldName}
                name={fieldName}
                label={label}
                defaultValue={defaultValue}
                type={type}
                hintText={hintText}
                errorMessage={errors[fieldName]?.message as string}
                register={register}
                orderIndex={orderIndex}
                style={style}
                props={props}
                onChange={onChange as () => void}
                extraContent={extraContent}
                placeholder={placeholder}
                capitalLetters={capitalLetters}
                onlyLettersAndNumbers={onlyLettersAndNumbers}
                onlyNumbers={onlyNumbers}
                onInput={onInput as (value: string) => string}
              />
            );
          }
        )}
      {elements}
      {withSubmitButton && (
        <Button type="submit" form={formID}>
          {submitLabel}
        </Button>
      )}
    </form>
  );
};

export default Form;
